import React, {FC} from 'react';
import {
  EducationalInfo,
  InputQuestion,
  SectionSpacing,
  TemperatureSlider,
  TemperatureUnits,
  useComponent,
  generateTestId,
  TestIdElementType,
  useFeatureAppId,
  YesNoToggle,
  YesNoValues,
} from '@volkswagen-onehub/whitelabel-e-mobility-commons';
import {useTranslation} from '../use-translation';
import {useTenantConfiguration} from '../use-tenant-configuration';
import {ChargeTimeCalculationFormFields} from '../use-charge-calculation-form';
import {ChargingSelection} from './charging-selection';
import styled from 'styled-components';

interface InputSectionProps {
  chargingLocationField: ChargeTimeCalculationFormFields['chargingLocation'];
  outsideTemperatureField: ChargeTimeCalculationFormFields['outsideTemperature'];
  chargeDeltaField: ChargeTimeCalculationFormFields['chargeDelta'];
  /** max value the start-charge thumb can have */
  chargeStartMax: number;
  /** min value the final-charge thumb can have */
  chargeFinalMin: number;
  chargerTypeField: ChargeTimeCalculationFormFields['chargerType'];
  chargingPowerField: ChargeTimeCalculationFormFields['chargingPower'];
  preHeatedBatteryField: ChargeTimeCalculationFormFields['preHeatedBattery'];
  disablePreHeatedToggle?: boolean;
}
export const InputSection: FC<InputSectionProps> = React.memo(
  ({
    outsideTemperatureField,
    chargeDeltaField,
    chargingLocationField,
    chargeStartMax,
    chargeFinalMin,
    chargerTypeField,
    chargingPowerField,
    preHeatedBatteryField,
    disablePreHeatedToggle,
  }: InputSectionProps) => {
    const featureAppId = useFeatureAppId();
    const translations = useTranslation();
    const tenantConfig = useTenantConfiguration();
    const MultiSlider = useComponent('MultiSlider');
    const Divider = useComponent('Divider');
    const Text = useComponent('Text');
    const temperatureMinMaxDefaults = {min: -20, max: 30};

    function getTemperatureUnit(): TemperatureUnits {
      const unit = tenantConfig.global.temperatureUnit;
      switch (unit) {
        case 'Celsius':
          return TemperatureUnits.celsius;
        case 'Fahrenheit':
          return TemperatureUnits.fahrenheit;
      }
    }

    const tempUnit = getTemperatureUnit();
    const getTemperatureValueText = React.useCallback(
      (value: number) =>
        translations.inputSection.temperatureOptions[tempUnit].replace(
          '%%temperature%%',
          `${value >= 0 ? '+' : '-'}${Math.abs(value)}`
        ),
      [translations, tempUnit]
    );

    const chargeDeltaSlider = React.useMemo(
      () => (
        <MultiSlider
          getValueText={(value: number) => `${value} %`}
          step={10}
          min={0}
          max={100}
          minStepBetweenThumbs={1}
          labelId={chargeDeltaField.labelId}
          thumbs={[
            {
              defaultValue: chargeDeltaField.value[0],
              ariaLabel: translations.inputSection.socOptions.current,
              max: chargeStartMax,
            },
            {
              defaultValue: chargeDeltaField.value[1],
              ariaLabel: translations.inputSection.socOptions.goal,
              min: chargeFinalMin,
            },
          ]}
          highlight={{
            value: 80,
            label:
              translations.inputSection.socRecommendationMarker
                .labelRecommendation,
          }}
          onChangeImmediate={
            chargeDeltaField.onChangeWithoutSideEffect as (v: number[]) => void
          }
          onChange={chargeDeltaField.onChange as (v: number[]) => void}
          testId={generateTestId({
            featureAppId,
            elementName: 'charge-data-slider',
            elementType: TestIdElementType.dynamic,
          })}
        />
      ),
      // eslint-disable-next-line react-hooks/exhaustive-deps -- We don't want to update the defaultValues on each change, as it resets the slider states
      [
        chargeDeltaField.labelId,
        translations,
        chargeFinalMin,
        chargeStartMax,
        chargeDeltaField.onChange,
      ]
    );

    const chargeTemperatureSlider = React.useMemo(
      () => (
        <TemperatureSlider
          tempUnit={tempUnit}
          defaultValue={outsideTemperatureField.value}
          ariaLabelledby={outsideTemperatureField.labelId}
          getValueText={getTemperatureValueText}
          onChangeImmediate={
            outsideTemperatureField.onChangeWithoutSideEffect as (
              value: number
            ) => void
          }
          onChange={outsideTemperatureField.onChange as (value: number) => void}
          range={
            tenantConfig.inputSection.temperatureQuestion.range ??
            temperatureMinMaxDefaults
          }
        />
      ),
      // eslint-disable-next-line react-hooks/exhaustive-deps -- We don't want to update the defaultValues on each change, as it resets the slider states
      [
        outsideTemperatureField.onChange,
        outsideTemperatureField.onChangeWithoutSideEffect,
        outsideTemperatureField.labelId,
        translations,
      ]
    );

    const showPreHeatedDataToggle =
      tenantConfig.featureFlags?.showPreHeatedDataToggle &&
      translations.inputSection.preHeatedBatteryQuestion;

    return (
      <section aria-label={translations.inputSection.ariaLabel}>
        <ChargingSelection
          chargerTypeField={chargerTypeField}
          chargingLocationField={chargingLocationField}
          chargingPowerField={chargingPowerField}
        />

        <SectionSpacing spacingTop={false} spacingBottom={true}>
          <Divider />
        </SectionSpacing>

        <InputQuestion
          questionTextId={chargeDeltaField.labelId}
          text={chargeDeltaField.label}
          infoButton={
            <EducationalInfo
              translations={translations.inputSection.socQuestion}
              globalTranslations={translations.global}
              faqSectionTranslations={translations.faqSection}
              faqOrLink={tenantConfig.inputSection.socQuestion}
            />
          }
        >
          {chargeDeltaSlider}
        </InputQuestion>

        {showPreHeatedDataToggle && (
          <SectionSpacing spacingTop={false} spacingBottom={true}>
            <Divider />
          </SectionSpacing>
        )}

        <InputQuestion
          questionTextId={outsideTemperatureField.labelId}
          text={outsideTemperatureField.label}
          containerSpacingBottom={!!showPreHeatedDataToggle}
          infoButton={
            <EducationalInfo
              translations={translations.inputSection.temperatureQuestion}
              globalTranslations={translations.global}
              faqSectionTranslations={translations.faqSection}
              faqOrLink={tenantConfig.inputSection.temperatureQuestion}
            />
          }
        >
          {chargeTemperatureSlider}
        </InputQuestion>

        {showPreHeatedDataToggle && (
          <InputQuestion
            text={preHeatedBatteryField.label}
            questionTextId={preHeatedBatteryField.labelId}
            containerSpacingBottom={false}
            infoButton={
              <EducationalInfo
                translations={
                  translations.inputSection.preHeatedBatteryQuestion
                }
                globalTranslations={translations.global}
                faqSectionTranslations={translations.faqSection}
                testId={generateTestId({
                  featureAppId,
                  elementName: 'pre-heated-battery-question',
                  elementType: TestIdElementType.button,
                })}
              />
            }
          >
            <YesNoToggle
              id="pre-heated-battery-toggle"
              questionTextId={preHeatedBatteryField.labelId}
              value={preHeatedBatteryField.value}
              onChange={preHeatedBatteryField.onChange}
              responseLabels={translations.global.responseLabels}
              testId={generateTestId({
                featureAppId,
                elementName: 'pre-heated-battery',
                elementType: TestIdElementType.toggle,
              })}
              disabled={disablePreHeatedToggle}
            />
            {disablePreHeatedToggle && (
              <TextWrapper>
                <Text tag="p" variant="label-small">
                  {preHeatedBatteryField.value === YesNoValues.yes
                    ? translations.inputSection.onlyPreHeatedDataAvailable
                    : translations.inputSection.onlyNonPreHeatedDataAvailable}
                </Text>
              </TextWrapper>
            )}
          </InputQuestion>
        )}
      </section>
    );
  }
);

InputSection.displayName = 'InputSection';

const TextWrapper = styled.div`
  margin-top: ${(props) =>
    props.theme.brand.elements.preHeatedBattery?.marginTop};

  & > p {
    color: ${(props) =>
      props.theme.brand.elements.preHeatedBattery?.disabledTextColor};
  }
`;
